/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { StockItem } from "../Stock/types";
import { toBaseQty } from "../Stock/units";
import type { EditableReceiptLine } from "../Purchases/types";
import { ReceiptLineRow } from "../Purchases/ReceiptLineRow";
import type { Order, OrderDraft } from "./types";
import { poNumber } from "./types";
import { orderText } from "./orderText";
import { OrderStatusBadge } from "./OrderStatusBadge";

type Supplier = { id: number; name: string; email: string | null };

type Props = {
  /** Existing order to open, or null for a new one. */
  orderId: number | null;
  /** A new order pre-filled from a scan or the shopping list. */
  initial?: OrderDraft | null;
  onClose: (changed: boolean) => void;
  /** Open the delivery (goods receipt) to confirm what arrived. */
  onReceive: (receiptId: number) => void;
};

const emptyLine = (): EditableReceiptLine => ({
  supplier_item_id: null,
  stock_item_id: null,
  description: "",
  supplier_code: null,
  gtin: null,
  quantity: 0,
  unit: "",
  unit_price: 0,
  price_confirmed: false,
});

// Order from a supplier outside Lemmon: lines, expected delivery, send by
// email; then receive it as a delivery. Optionally creates the draft delivery
// right away. See docs/plans/inventory_and_costing/05_purchasing.md.
export const PurchaseOrderEditor = defineComponent<Props>({
  name: "PurchaseOrderEditor",
  props: ["orderId", "initial", "onClose", "onReceive"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const busy = ref(false);
    const changed = ref(false);
    const order = ref<Order | null>(null);
    const supplierId = ref<number | null>(null);
    const reference = ref("");
    const expectedAt = ref("");
    const notes = ref("");
    const documentKey = ref<string | null>(null);
    const lines = ref<EditableReceiptLine[]>([emptyLine()]);
    const createReceipt = ref(true);
    const stockItems = ref<StockItem[]>([]);
    const suppliers = ref<Supplier[]>([]);

    const status = computed(() => order.value?.status ?? "draft");
    const hasArrivals = computed(() => order.value?.receipts.some((r) => r.status === "posted") ?? false);
    const editable = computed(() => (status.value === "draft" || status.value === "sent") && !hasArrivals.value);

    const apply = (o: Order) => {
      order.value = o;
      supplierId.value = o.supplier_id;
      reference.value = o.reference ?? "";
      expectedAt.value = o.expected_at?.slice(0, 10) ?? "";
      notes.value = o.notes ?? "";
      documentKey.value = o.document_key;
      lines.value = o.lines.map((l) => ({
        supplier_item_id: l.supplier_item_id,
        stock_item_id: l.stock_item_id,
        description: l.description ?? "",
        supplier_code: l.supplier_code,
        gtin: null,
        quantity: l.quantity,
        unit: l.unit,
        unit_price: l.unit_price,
        price_confirmed: false,
      }));
    };

    onMounted(async () => {
      try {
        const [items, sups, existing] = await Promise.all([
          trpc.stock.list.query(),
          trpc.purchasing.suppliers.query(),
          props.orderId ? trpc.purchaseOrders.get.query({ id: props.orderId }) : null,
        ]);
        stockItems.value = (items as StockItem[]).filter((s) => s.active);
        suppliers.value = sups.filter((s) => s.active);
        if (existing) apply(existing as Order);
        else if (props.initial) {
          const d = props.initial;
          supplierId.value = d.supplier_id;
          reference.value = d.reference ?? "";
          expectedAt.value = d.expected_at ?? "";
          documentKey.value = d.document_key;
          lines.value = d.lines.map((l) => ({ ...l, gtin: null, price_confirmed: false }));
        }
      } catch {
        toast.showToast(t("inventory.orders.load_failed"), { type: "error" });
        props.onClose(false);
      } finally {
        loading.value = false;
      }
    });

    const itemFor = (l: EditableReceiptLine) => stockItems.value.find((s) => s.id === l.stock_item_id);
    const filled = computed(() => lines.value.filter((l) => l.stock_item_id && l.quantity > 0));
    const unmapped = computed(() => lines.value.filter((l) => !l.stock_item_id && (l.description || l.quantity > 0)).length);
    const invalid = computed(() =>
      filled.value.some((l) => {
        const item = itemFor(l);
        return !item || toBaseQty(item, 1, l.unit) == null;
      }),
    );
    const total = computed(() => filled.value.reduce((s, l) => s + l.quantity * l.unit_price, 0));
    const canSave = computed(
      () => !loading.value && !busy.value && !invalid.value && unmapped.value === 0 && filled.value.length > 0,
    );

    const save = async (nextStatus?: "draft" | "sent") => {
      const isNew = !order.value;
      const saved = (await trpc.purchaseOrders.save.mutate({
        id: order.value?.id,
        supplier_id: supplierId.value,
        reference: reference.value.trim() || null,
        expected_at: expectedAt.value || null,
        notes: notes.value.trim() || null,
        document_key: documentKey.value,
        status: isNew ? nextStatus ?? "draft" : undefined,
        create_receipt: isNew ? createReceipt.value : undefined,
        lines: filled.value.map((l) => ({
          supplier_item_id: l.supplier_item_id,
          stock_item_id: l.stock_item_id!,
          description: l.description || null,
          supplier_code: l.supplier_code,
          quantity: l.quantity,
          unit: l.unit,
          unit_price: l.unit_price,
        })),
      })) as Order;
      changed.value = true;
      if (!isNew && nextStatus && saved.status !== nextStatus) {
        apply((await trpc.purchaseOrders.setStatus.mutate({ id: saved.id, status: nextStatus })) as Order);
      } else {
        apply(saved);
      }
      return order.value!;
    };

    const run = async (action: () => Promise<void>, failKey = "inventory.orders.save_failed") => {
      busy.value = true;
      try {
        await action();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t(failKey), { type: "error" });
      } finally {
        busy.value = false;
      }
    };

    const supplierEmail = computed(() => suppliers.value.find((s) => s.id === supplierId.value)?.email ?? null);

    const mailto = (o: Order) =>
      `mailto:${encodeURIComponent(o.supplier_email ?? supplierEmail.value ?? "")}?subject=${encodeURIComponent(
        `${t("inventory.orders.email_subject")} ${poNumber(o.order_number)}`,
      )}&body=${encodeURIComponent(
        orderText(o, { heading: t("inventory.orders.email_subject"), deliver_by: t("inventory.orders.deliver_by") }),
      )}`;

    const handleSave = (nextStatus?: "draft" | "sent") =>
      run(async () => {
        const saved = await save(nextStatus);
        toast.showToast(t("inventory.orders.saved"), { type: "success" });
        if (nextStatus === "sent" && (saved.supplier_email ?? supplierEmail.value)) {
          window.location.href = mailto(saved);
        }
      });

    const receive = () =>
      run(async () => {
        if (editable.value) await save();
        const { receipt_id } = await trpc.purchaseOrders.receive.mutate({ id: order.value!.id });
        changed.value = true;
        props.onReceive(receipt_id);
      });

    const cancelOrder = () =>
      run(async () => {
        if (!confirm(t("inventory.orders.cancel_confirm"))) return;
        apply((await trpc.purchaseOrders.setStatus.mutate({ id: order.value!.id, status: "cancelled" })) as Order);
        changed.value = true;
      });

    const field = (label: string, input: unknown) => (
      <label class="block">
        <span class="mb-1 block text-sm font-medium">{label}</span>
        {input}
      </label>
    );

    const renderProgress = () => {
      const o = order.value;
      if (!o || editable.value) return null;
      return (
        <div class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>{t("inventory.recipe.col_item")}</th>
                <th class="text-right">{t("inventory.orders.col_ordered")}</th>
                <th class="text-right">{t("inventory.orders.col_received")}</th>
                <th class="text-right">{t("inventory.purchases.col_total")}</th>
              </tr>
            </thead>
            <tbody>
              {o.lines.map((l) => {
                const done = l.outstanding_qty <= 0;
                return (
                  <tr key={l.id}>
                    <td>
                      <div class="font-medium">{l.stock_item_name}</div>
                      {l.description && <div class="text-xs text-base-content/50">{l.description}</div>}
                    </td>
                    <td class="text-right">{`${Number(l.quantity)} × ${l.unit}`}</td>
                    <td class={["text-right", done ? "text-success" : l.received_qty > 0 ? "text-warning" : "text-base-content/50"].join(" ")}>
                      {`${Number(l.received_qty)} × ${l.unit}`}
                      {done && <span class="material-symbols-outlined ml-1 align-middle text-sm">check</span>}
                    </td>
                    <td class="text-right">{l.line_total.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    };

    const renderBody = () => {
      if (loading.value) {
        return (
          <div class="flex justify-center p-10">
            <Loading />
          </div>
        );
      }
      if (stockItems.value.length === 0) {
        return <div class="p-6 text-sm text-base-content/70">{t("inventory.recipe.no_stock_items")}</div>;
      }
      const o = order.value;
      return (
        <div class="space-y-4 p-6">
          {o && (
            <div class="flex flex-wrap items-center gap-3">
              <span class="font-mono text-lg font-semibold">{poNumber(o.order_number)}</span>
              <OrderStatusBadge status={o.status} />
              {o.sent_at && (
                <span class="text-xs text-base-content/50">
                  {t("inventory.orders.sent_on").replace("{date}", new Date(o.sent_at).toLocaleDateString())}
                </span>
              )}
            </div>
          )}
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {field(
              t("inventory.purchases.supplier"),
              <select
                class="select select-bordered select-sm w-full"
                disabled={!editable.value}
                value={supplierId.value ?? ""}
                onChange={(e: Event) => {
                  const v = (e.target as HTMLSelectElement).value;
                  supplierId.value = v ? Number(v) : null;
                }}
              >
                <option value="">{t("inventory.purchases.no_supplier")}</option>
                {suppliers.value.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>,
            )}
            {field(
              t("inventory.orders.expected_at"),
              <input type="date" class="input input-bordered input-sm w-full" disabled={!editable.value} v-model={expectedAt.value} />,
            )}
            {field(
              t("inventory.orders.reference"),
              <input
                class="input input-bordered input-sm w-full"
                placeholder={t("inventory.orders.reference_placeholder")}
                disabled={!editable.value}
                v-model={reference.value}
              />,
            )}
          </div>

          {editable.value ? (
            <>
              <div class="overflow-x-auto">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>{t("inventory.recipe.col_item")}</th>
                      <th>{t("inventory.recipe.col_qty")}</th>
                      <th>{t("inventory.recipe.col_unit")}</th>
                      <th>{t("inventory.purchases.col_unit_price")}</th>
                      <th class="text-right">{t("inventory.purchases.col_total")}</th>
                      <th class="text-right">{t("inventory.purchases.col_change")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.value.map((line, index) => (
                      <ReceiptLineRow
                        key={index}
                        line={line}
                        stockItems={stockItems.value}
                        readOnly={false}
                        noHold
                        onChange={(next: EditableReceiptLine) =>
                          (lines.value = lines.value.map((l, i) => (i === index ? next : l)))
                        }
                        onRemove={() => (lines.value = lines.value.filter((_, i) => i !== index))}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <button class="btn btn-ghost btn-sm" onClick={() => (lines.value = [...lines.value, emptyLine()])}>
                <span class="material-symbols-outlined text-base">add</span>
                {t("inventory.purchases.add_line")}
              </button>
            </>
          ) : (
            renderProgress()
          )}

          <div class="flex flex-wrap items-end justify-between gap-4 border-t border-base-300 pt-4">
            <label class="block w-full max-w-sm">
              <span class="mb-1 block text-sm font-medium">{t("inventory.purchases.notes")}</span>
              <textarea
                class="textarea textarea-bordered w-full text-sm"
                rows={2}
                placeholder={t("inventory.orders.notes_placeholder")}
                disabled={!editable.value}
                v-model={notes.value}
              />
            </label>
            <div class="text-right">
              <div class="text-sm text-base-content/60">{t("inventory.purchases.col_total")}</div>
              <div class="text-2xl font-semibold">{(o && !editable.value ? o.total : total.value).toFixed(2)}</div>
              <div class="text-xs text-base-content/50">{t("inventory.purchases.excl_vat")}</div>
            </div>
          </div>

          {!o && (
            <label class="flex cursor-pointer items-start gap-3 rounded-lg border border-base-300 bg-base-200/60 p-3">
              <input type="checkbox" class="toggle toggle-primary toggle-sm mt-0.5" v-model={createReceipt.value} />
              <span>
                <span class="block text-sm font-medium">{t("inventory.orders.create_receipt")}</span>
                <span class="block text-xs text-base-content/60">{t("inventory.orders.create_receipt_help")}</span>
              </span>
            </label>
          )}

          {o && o.receipts.length > 0 && (
            <div class="rounded-lg border border-base-300">
              <div class="border-b border-base-300 px-3 py-2 text-sm font-medium">{t("inventory.orders.deliveries")}</div>
              <ul class="divide-y divide-base-200">
                {o.receipts.map((r) => (
                  <li key={r.id}>
                    <button class="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-base-200/60" onClick={() => props.onReceive(r.id)}>
                      <span class="flex-1">{new Date(r.received_at).toLocaleDateString()}</span>
                      <span class={r.status === "posted" ? "badge badge-success badge-sm" : "badge badge-ghost badge-sm"}>
                        {t(`inventory.purchases.status_${r.status}`)}
                      </span>
                      <span class="w-20 text-right font-medium">{r.total.toFixed(2)}</span>
                      <span class="material-symbols-outlined text-base text-base-content/40">chevron_right</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {unmapped.value > 0 && (
            <div class="alert alert-warning py-2 text-sm">
              {t("inventory.purchases.unmapped_help").replace("{count}", String(unmapped.value))}
            </div>
          )}
          {documentKey.value && (
            <a
              class="link link-primary inline-flex items-center gap-1 text-sm"
              href={`/api/purchasing/documents/${encodeURIComponent(documentKey.value)}`}
              target="_blank"
              rel="noopener"
            >
              <span class="material-symbols-outlined text-base">description</span>
              {t("inventory.scan.view_document")}
            </a>
          )}
        </div>
      );
    };

    const renderButtons = () => {
      const o = order.value;
      const receivable = o && ["sent", "partially_received", "draft"].includes(o.status);
      return (
        <div class="border-t p-4">
          <div class="flex flex-wrap justify-between gap-2">
            <div class="flex gap-2">
              <button class="btn btn-neutral" onClick={() => props.onClose(changed.value)}>
                {t("inventory.purchases.close")}
              </button>
              {o && (o.status === "draft" || o.status === "sent") && (
                <button class="btn btn-ghost text-error" disabled={busy.value} onClick={cancelOrder}>
                  {t("inventory.orders.cancel_order")}
                </button>
              )}
            </div>
            <div class="flex flex-wrap gap-2">
              {o && o.status === "sent" && (o.supplier_email ?? supplierEmail.value) && (
                <a class="btn btn-ghost" href={mailto(o)}>
                  <span class="material-symbols-outlined text-base">mail</span>
                  {t("inventory.orders.email_again")}
                </a>
              )}
              {editable.value && (
                <button class="btn btn-ghost" disabled={!canSave.value} onClick={() => handleSave()}>
                  {o ? t("inventory.orders.save") : t("inventory.orders.save_draft")}
                </button>
              )}
              {editable.value && status.value === "draft" && (
                <button class="btn btn-neutral" disabled={!canSave.value} onClick={() => handleSave("sent")}>
                  <span class="material-symbols-outlined text-base">send</span>
                  {supplierEmail.value ? t("inventory.orders.send_email") : t("inventory.orders.mark_sent")}
                </button>
              )}
              {receivable && (
                <button class="btn btn-primary" disabled={busy.value || (editable.value && !canSave.value)} onClick={receive}>
                  <span class="material-symbols-outlined text-base">inventory</span>
                  {t("inventory.orders.receive")}
                </button>
              )}
            </div>
          </div>
        </div>
      );
    };

    return () => (
      <LemmonModal
        title={order.value ? t("inventory.orders.edit_title") : t("inventory.orders.new_title")}
        onClose={() => props.onClose(changed.value)}
        renderButtons={renderButtons}
      >
        {renderBody()}
      </LemmonModal>
    );
  },
});
